import React, { useEffect, useState } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { contactInfo } from '../data';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Breadcrumb } from '../components/Breadcrumb';
import emailjs from '@emailjs/browser';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';

const Contact = ({ onQuotationClick }) => {
  const navigate = useNavigate();
  const [hasShownModal, setHasShownModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!hasShownModal) {
      const timer = setTimeout(() => {
        onQuotationClick();
        setHasShownModal(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [onQuotationClick, hasShownModal]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          name: `${data.firstName} ${data.lastName}`,
          from_email: data.email,
          phone: data.phone,
          subject: data.subject,
          title: data.subject,
          message: data.message,
          time: new Date().toLocaleString(),
          to_email: 'contact@aashigroups.in'
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      toast.success('Message sent successfully! We will respond within 24 hours.');
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: 'conversion', conversion_type: 'contact' });
      }
      e.target.reset();
      navigate('/thank-you', { state: { type: 'contact' }, replace: true });
    } catch (error) {
      console.error('EmailJS Error:', error);
      toast.error('Failed to send. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <SEO
        title="Contact Us"
        description="Get in touch with Aashi Constructions in Bangalore. Call, email, or visit us to discuss your construction needs. Free consultation available."
        keywords="contact aashi constructions, bangalore construction company, get quote construction"
        url="/contact"
      />
      <Breadcrumb items={[{ label: 'Contact' }]} />
      
      {/* Hero Section */}
      <section className="relative h-64 sm:h-80 md:h-96 flex items-center justify-center overflow-hidden">
        <img
          src="https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg"
          alt="Contact"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        <div className="relative z-10 text-center px-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-3 sm:mb-4">
            Get In Touch
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
            Start your construction journey today. Our team is ready to help bring your project to life.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
                Let's Connect
              </h2>
              <p className="text-base md:text-lg text-gray-600 mb-10 leading-relaxed">
                Whether you're planning a new build, renovation, or simply exploring possibilities, we're here to help. Reach out to schedule a private consultation.
              </p>

              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Visit Our Office</h3>
                    <p className="text-gray-600">{contactInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Call Us</h3>
                    {contactInfo.phones.map((phone, i) => (
                      <a
                        key={i}
                        href={`tel:${phone.replace(/\s/g, '')}`}
                        className="text-gray-600 hover:text-brand-600 transition-colors duration-200 block"
                      >
                        {phone}
                      </a>
                    ))}
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Email Us</h3>
                    {contactInfo.emails.map((email, i) => (
                      <a
                        key={i}
                        href={`mailto:${email}`}
                        className="text-gray-600 hover:text-brand-600 transition-colors duration-200 block"
                      >
                        {email}
                      </a>
                    ))}
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Office Hours</h3>
                    <p className="text-gray-600">{contactInfo.hours}</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-4 sm:p-6 md:p-8 bg-gradient-to-br from-brand-50 to-white rounded-2xl border border-brand-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Request a Private Quotation
                </h3>
                <p className="text-gray-600 mb-6">
                  Get a detailed, personalized quotation for your construction project. Our team will review your requirements and provide a comprehensive proposal.
                </p>
                <Button
                  onClick={onQuotationClick}
                  className="w-full bg-brand-600 hover:bg-brand-700 text-white"
                >
                  Open Quotation Form
                </Button>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100">
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
                Send a Message
              </h2>
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <Input
                      name="firstName"
                      placeholder="Aashi"
                      required
                      className="border-gray-300 focus:border-brand-600 focus:ring-brand-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <Input
                      name="lastName"
                      placeholder="Construction"
                      required
                      className="border-gray-300 focus:border-brand-600 focus:ring-brand-600"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    placeholder="aashi@example.com"
                    required
                    className="border-gray-300 focus:border-brand-600 focus:ring-brand-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="+91 98765 43210"
                    required
                    className="border-gray-300 focus:border-brand-600 focus:ring-brand-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <Input
                    name="subject"
                    placeholder="Inquiry about custom home design"
                    required
                    className="border-gray-300 focus:border-brand-600 focus:ring-brand-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <Textarea
                    name="message"
                    placeholder="Tell us about your project..."
                    rows={5}
                    required
                    className="border-gray-300 focus:border-brand-600 focus:ring-brand-600 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-brand-600 hover:bg-brand-700 text-white py-4 md:py-6 text-base md:text-lg"
                >
                  Send Message
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  We respect your privacy and will never share your information. Response time: within 24 hours.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="w-full">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3888.184577453677!2d77.48698807507625!3d12.960037987354287!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTLCsDU3JzM2LjEiTiA3N8KwMjknMjIuNCJF!5e0!3m2!1sen!2sin!4v1778674465894!5m2!1sen!2sin"
          width="100%"
          height="450"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Aashi Constructions Location"
          className="w-full"
        />
      </section>
    </div>
  );
};

export default Contact;
